import { Home } from "lucide-react";
import { type FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CurrentUser } from "../../hooks/useAuthRestApi";
import { canAccessAdminPages } from "../../utils/admin-utils";
import { isAllowDevelopUse, isTenantAdmin } from "../../utils/authority-utils";
import { DropdownMenuUI } from "../DropdownMenu";
import { Separator } from "../ui/separator";
import SideBarMenuItemUI from "./SideBarMenuItem";

interface SideBarMenuProps {
  currentUser?: CurrentUser | null;
  currentPath: string;
  isSidebarOpen?: boolean;
}

interface Menu {
  id: string;
  title: string;
  to: string;
  display: boolean;
  section?: string;
}

export const SideBarMenuUI: FC<SideBarMenuProps> = ({
  currentUser,
  currentPath,
  isSidebarOpen = false,
}) => {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [isHomeHovered, setIsHomeHovered] = useState(false);
  const [homeTooltipPosition, setHomeTooltipPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isUserHovered, setIsUserHovered] = useState(false);
  const [userTooltipPosition, setUserTooltipPosition] = useState({
    top: 0,
    left: 0,
  });
  const organizationId = currentUser?.organizationId ?? null;
  const isAdmin = isTenantAdmin(currentUser ?? null);

  const onClickUser = () => {
    setShowDropdownMenu(true);
  };

  const onHiddenDropdownMenu = () => {
    setShowDropdownMenu(false);
  };

  const handleHomeMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHomeTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setIsHomeHovered(true);
  };

  const handleUserMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setUserTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setIsUserHovered(true);
  };

  // メニュー配列をuseMemoで最適化
  const menus: Menu[] = useMemo(
    () => [
      {
        id: "emailsSearch",
        title: "メール検索",
        to: "/emails/search",
        display: true,
        section: "メール検索",
      },
      {
        id: "matching",
        title: "人材マッチング",
        to: "/matching",
        display: true,
        section: "マッチング",
      },
      {
        id: "bulkEmail",
        title: "メール一斉配信",
        to: "/emails/bulk",
        display: isAllowDevelopUse(organizationId),
        section: "メール配信",
      },
      {
        id: "emailsSchedules",
        title: "配信予約一覧",
        to: "/emails/schedules",
        display: isAllowDevelopUse(organizationId),
        section: "メール配信",
      },
      {
        id: "emailsHistories",
        title: "配信履歴",
        to: "/emails/histories",
        display: isAllowDevelopUse(organizationId),
        section: "メール配信",
      },
      {
        id: "distributionGroups",
        title: "配信グループ",
        to: "/admin/distribution/groups",
        display: isAllowDevelopUse(organizationId),
        section: "メール配信",
      },
      {
        id: "invitations",
        title: "メンバー招待",
        to: "/settings/invitations",
        display: isAdmin,
        section: "設定",
      },
      {
        id: "admin",
        title: "管理画面",
        to: "/admin",
        display: canAccessAdminPages(currentUser ?? null),
        section: "管理",
      },
    ],
    [organizationId, isAdmin, currentUser],
  );

  // セクションごとにグループ化（useMemoで最適化）
  const groupedMenus = useMemo(
    () =>
      menus.reduce(
        (acc, menu) => {
          if (!menu.display) return acc;
          const section = menu.section || "その他";
          if (!acc[section]) {
            acc[section] = [];
          }
          acc[section].push(menu);
          return acc;
        },
        {} as Record<string, Menu[]>,
      ),
    [menus],
  );

  return (
    <div className="flex flex-col h-full px-3">
      <div className="relative">
        <Link
          to="/"
          className={`flex items-center space-x-2 px-2 py-3 mb-2 rounded-lg transition-colors ${
            currentPath === "/"
              ? "bg-white/20 text-white"
              : "text-white/80 hover:bg-white/10 hover:text-white"
          }`}
          onMouseEnter={!isSidebarOpen ? handleHomeMouseEnter : undefined}
          onMouseLeave={
            !isSidebarOpen ? () => setIsHomeHovered(false) : undefined
          }
        >
          <Home className="w-5 h-5 shrink-0" />
          {isSidebarOpen && (
            <span className="font-semibold text-sm">ホーム</span>
          )}
        </Link>
        {!isSidebarOpen && isHomeHovered && (
          <div
            className="fixed text-xs whitespace-nowrap pointer-events-none px-2.5 py-1 bg-gradient-to-r from-sidebar-left to-sidebar-right text-white rounded-lg shadow-lg border border-white/20 z-50"
            style={{
              top: `${homeTooltipPosition.top}px`,
              left: `${homeTooltipPosition.left}px`,
              transform: "translateY(-50%)",
            }}
          >
            ホーム
          </div>
        )}
      </div>
      <Separator className="mb-3 bg-white/10" />
      <nav className="flex-1 overflow-y-auto space-y-1">
        {Object.entries(groupedMenus).map(
          ([section, sectionMenus], sectionIndex) => (
            <div key={section}>
              {sectionIndex > 0 && <Separator className="my-3 bg-white/10" />}
              <ul className="mt-1 space-y-0.5">
                {sectionMenus.map(menu => (
                  <SideBarMenuItemUI
                    key={menu.id}
                    id={menu.id}
                    title={menu.title}
                    to={menu.to}
                    isActive={currentPath === menu.to}
                    isSidebarOpen={isSidebarOpen}
                  />
                ))}
              </ul>
            </div>
          ),
        )}
      </nav>
      {currentUser && (
        <div className="relative shrink-0 pb-4 pt-3 border-t border-white/10 mt-3">
          <div
            className="flex flex-row items-center space-x-2 cursor-pointer px-2 py-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={onClickUser}
            onMouseEnter={!isSidebarOpen ? handleUserMouseEnter : undefined}
            onMouseLeave={
              !isSidebarOpen ? () => setIsUserHovered(false) : undefined
            }
          >
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-medium shrink-0">
              {currentUser.familyName?.charAt(0) || "U"}
            </div>
            {isSidebarOpen && (
              <span
                className="text-sm text-white truncate"
                data-testid="sidebar-username"
              >
                {currentUser.familyName} {currentUser.givenName}
              </span>
            )}
          </div>
          {!isSidebarOpen && isUserHovered && !showDropdownMenu && (
            <div
              className="fixed text-xs whitespace-nowrap pointer-events-none px-2.5 py-1 bg-gradient-to-r from-sidebar-left to-sidebar-right text-white rounded-lg shadow-lg border border-white/20 z-50"
              style={{
                top: `${userTooltipPosition.top}px`,
                left: `${userTooltipPosition.left}px`,
                transform: "translateY(-50%)",
              }}
            >
              {currentUser.familyName} {currentUser.givenName}
            </div>
          )}
          <DropdownMenuUI
            visibility={showDropdownMenu}
            onHidden={onHiddenDropdownMenu}
          />
        </div>
      )}
    </div>
  );
};
